import { useEffect, useState } from 'react';

import { useSelector, useDispatch } from 'react-redux';

import CustomThemeButton from '@atlaskit/button/custom-theme-button';
import { JiraIcon } from '@atlaskit/logo';
import Select, { FormatOptionLabelMeta } from '@atlaskit/select';
import { gridSize } from '@atlaskit/theme';

import IconWithFallback from 'src/components/icon-with-fallback';
import { LoadingStatus } from 'src/constants/loading-status';
import { useIntl } from 'src/hooks/intl';
import {
  fetchJiraSites,
  fetchJiraProjects,
  linkJiraProject,
} from 'src/redux/jira/actions';
import {
  getJiraSites,
  getJiraSitesFetchedStatus,
  getJiraProjects,
  getLinkJiraProjectStatus,
} from 'src/redux/jira/selectors/repo-page-selectors';
import { Project } from 'src/redux/jira/types';
import { getCurrentRepository } from 'src/selectors/repository-selectors';

import messages from './link-project.i18n';
import * as styles from './link-project.styled';

type SiteOption = {
  value: string;
  label: string;
};

type ProjectOption = {
  value: string;
  label: string;
  avatarUrl?: string;
  siteCloudId?: string;
  isDisabled?: boolean;
};

const formatOptionLabel = (
  option: ProjectOption,
  { context }: FormatOptionLabelMeta<ProjectOption>
) => {
  if (context === 'menu') {
    return (
      <styles.CustomOption>
        {option.avatarUrl && (
          <styles.CustomOptionAvatar>
            <IconWithFallback
              src={option.avatarUrl}
              size="xsmall"
              appearance="square"
              fallback={<JiraIcon size="xsmall" />}
            />
          </styles.CustomOptionAvatar>
        )}
        <div>{option.label}</div>
      </styles.CustomOption>
    );
  }
  return option.label;
};

const getSitesNoOptionsMessage = (
  fetchedStatus: LoadingStatus,
  sitesCount: number
) => {
  if (fetchedStatus === LoadingStatus.Failed) {
    return messages.siteSelectorFailedMessage;
  } else if (sitesCount === 0) {
    return messages.siteSelectorNoSitesMessage;
  }
  return undefined;
};

const getProjectsNoOptionsMessage = (
  fetchedStatus: LoadingStatus,
  projectsCount: number
) => {
  if (fetchedStatus === LoadingStatus.Failed) {
    return messages.projectSelectorFailedMessage;
  } else if (projectsCount === 0) {
    return messages.projectSelectorNoProjectsMessage;
  }
  return undefined;
};

type LinkProjectProps = {
  relevantProjects: Project[];
};

export const LinkProject = (props: LinkProjectProps) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const [selectedSite, setSelectedSite] = useState<SiteOption | null>();
  const [selectedProject, setSelectedProject] =
    useState<ProjectOption | null>();

  const selectedSiteCloudId = selectedSite?.value;

  const repository = useSelector(getCurrentRepository);

  const sites = useSelector(getJiraSites);
  const sitesFetchedStatus = useSelector(getJiraSitesFetchedStatus);

  const projectsData = useSelector(getJiraProjects);
  const projects = projectsData[selectedSiteCloudId!]?.projects || [];
  const projectsFetchedStatus =
    (selectedSite?.value && projectsData[selectedSite.value]?.fetchedStatus) ||
    LoadingStatus.Before;
  const projectsSize =
    (selectedSite?.value && projectsData[selectedSite.value]?.size) || 0;

  const linkJiraProjectStatus = useSelector(getLinkJiraProjectStatus);
  const isLinkingJiraProject = linkJiraProjectStatus === LoadingStatus.Fetching;

  useEffect(() => {
    // Fetch sites if not fetched already.
    if (sitesFetchedStatus === LoadingStatus.Before) {
      dispatch(fetchJiraSites());
    }
  }, [sitesFetchedStatus, dispatch]);

  useEffect(() => {
    // Fetch projects when the selected site changes.
    if (selectedSiteCloudId) {
      dispatch(
        fetchJiraProjects({
          cloudId: selectedSiteCloudId!,
          projectQuery: '',
        })
      );
    }
  }, [selectedSiteCloudId, dispatch]);

  useEffect(() => {
    // When a project is successfully linked clear the
    // selected site and project in the form.
    if (linkJiraProjectStatus === LoadingStatus.Success) {
      setSelectedSite(null);
      setSelectedProject(null);
    }
  }, [linkJiraProjectStatus]);

  const siteOptions: SiteOption[] = sites
    .filter(site => site.connected)
    .map(site => ({
      label: site.cloudName,
      value: site.cloudId,
    }));

  const projectOptions: ProjectOption[] = projects
    .map(project => ({
      label: project.name,
      value: project.id,
      avatarUrl: project.avatarUrls['128x128'] || project.avatarUrls['48x48'],
      siteCloudId: project.site.cloudId,
    }))
    .filter(project => {
      // Remove already linked projects.
      const alreadyLinked = props.relevantProjects.find(
        p => p.site.cloudId === project.siteCloudId && p.id === project.value
      );
      return !alreadyLinked;
    });

  const hasMoreProjects = projectsSize > projectOptions.length;
  if (hasMoreProjects) {
    projectOptions.push({
      label: intl.formatMessage(messages.typeToFilterProjectsMessage),
      value: '',
      isDisabled: true,
    });
  }

  const isSitesFetching = sitesFetchedStatus === LoadingStatus.Fetching;
  const isProjectsFetching = projectsFetchedStatus === LoadingStatus.Fetching;
  const sitesNoOptionsMessage = getSitesNoOptionsMessage(
    sitesFetchedStatus,
    siteOptions.length
  )!;
  const projectsNoOptionsMessage = getProjectsNoOptionsMessage(
    projectsFetchedStatus,
    projectOptions.length
  )!;

  const handleLinkJiraProject = () => {
    const project = projects.find(p => p.id === selectedProject?.value);
    if (project && repository) {
      dispatch(
        linkJiraProject({
          project,
          repository,
          included: true,
        })
      );
    }
  };

  // @ts-ignore TODO: fix noImplicitAny error here
  const handleInputChange = (inputVal: string, { action }) => {
    if (action === 'input-change') {
      dispatch(
        fetchJiraProjects({
          cloudId: selectedSiteCloudId!,
          projectQuery: inputVal,
        })
      );
    }
  };

  return (
    <styles.LinkProjectWrapper>
      <styles.SiteSelectorWrapper>
        <Select
          onChange={(selected, { action }) => {
            if (action === 'select-option') {
              setSelectedProject(null);
              setSelectedSite(selected as SiteOption);
            }
          }}
          value={selectedSite}
          options={siteOptions}
          isLoading={isSitesFetching}
          isDisabled={isSitesFetching}
          placeholder={intl.formatMessage(messages.siteSelectorPlaceholder)}
          noOptionsMessage={() =>
            sitesNoOptionsMessage && intl.formatMessage(sitesNoOptionsMessage)
          }
          // This is to make sure the select dropdown is visible outside the modal
          styles={{
            menuPortal: base => ({
              ...base,
              zIndex: 9999,
            }),
          }}
          menuPortalTarget={document.body}
          classNamePrefix="manage-projects-dialog-site-selector"
        />
      </styles.SiteSelectorWrapper>

      <styles.ProjectSelectorWrapper>
        <Select
          // We do our own filtering in handleInputChange, no need to filter again
          filterOption={null}
          onInputChange={handleInputChange}
          onChange={(selected, { action }) => {
            if (action === 'select-option') {
              setSelectedProject(selected as ProjectOption);
            }
          }}
          formatOptionLabel={formatOptionLabel}
          value={selectedProject}
          options={projectOptions}
          isLoading={isSitesFetching || isProjectsFetching}
          isDisabled={isSitesFetching || !selectedSite}
          placeholder={intl.formatMessage(messages.projectSelectorPlaceholder)}
          noOptionsMessage={() =>
            projectsNoOptionsMessage &&
            intl.formatMessage(projectsNoOptionsMessage)
          }
          // This is to make sure the select dropdown is visible outside the modal
          styles={{
            menuPortal: base => ({
              ...base,
              zIndex: 9999,
            }),
          }}
          menuPortalTarget={document.body}
          classNamePrefix="manage-projects-dialog-project-selector"
        />
      </styles.ProjectSelectorWrapper>
      <CustomThemeButton
        isLoading={isLinkingJiraProject}
        isDisabled={!(selectedSite && selectedProject) || isLinkingJiraProject}
        appearance="primary"
        onClick={handleLinkJiraProject}
        theme={(currentTheme, themeProps) => {
          const { buttonStyles, ...rest } = currentTheme(themeProps);
          return {
            buttonStyles: {
              ...buttonStyles,
              // This is to make the button to have the same height (40px) as the select dropdowns
              height: `${gridSize() * 5}px`,
              alignItems: 'center',
            },
            ...rest,
          };
        }}
        testId="manage-projects-dialog-link-action"
      >
        {intl.formatMessage(messages.linkActionButtonLabel)}
      </CustomThemeButton>
    </styles.LinkProjectWrapper>
  );
};
